import * as React from 'react';
import classnames from 'classnames';
import { Fabric, PrimaryButton, SearchBox } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { viewStyles } from 'styles/viewStyles';
import { formStyles } from 'styles/formStyles';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useParams } from 'react-router-dom';
import { RecordViewUrlParams } from 'layout/App/types';
import { Helmet } from 'react-helmet';
import { useKundeList } from 'api/kunde';
import { Lieferung } from 'api/lieferung';
import { Angebot } from 'api/angebot';
import { Auftrag } from 'api/auftrag';
import { Rechnung } from 'api/rechnung';
import { Attachment, deleteAttachment } from 'api/attachment';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List } from '@fluentui/react/lib/List';
import usePersistentState from 'hooks/usePersistentState';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import moment from 'moment-timezone';
import { createRecordUrl, ViewType } from 'utils/formatter';
import {
    AngebotStatusLabel,
    ServiceStatusLabel,
    AttachmentArtLabel,
    AuftragStatusLabel,
    LieferungStatusLabel,
    RechnungStatusLabel,
    SmRefTypeLabel,
    SmRefTypeView,
} from 'utils/labels';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { betreuerByNrSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { getApiUrl } from 'api/useApi';
import { AddAttachments } from 'components/History/AddAttachments';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { DeleteIcon, EditIcon } from '@fluentui/react-icons';
import { authSelector } from 'store/auth/selector';
import { EditAttachment } from 'components/History/EditAttachment';
import { Service } from 'api/service';

const styles = mergeStyleSets({
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px 120px 120px 120px 200px 1fr 200px',
        gridColumnGap: 10,
        marginTop: 10,
    },
    listItem: {
        display: 'grid',
        marginTop: 5,
        gridTemplateColumns: '140px 80px 100px 100px 100px 140px 1fr 1fr max-content',
        wordBreak: 'break-all',
    },
    listCell: {
        position: 'relative',
    },
    listHeader: {
        fontWeight: 700,
    },
    actionButtons: {
        display: 'grid',
        gridTemplateColumns: 'max-content max-content',
        gridColumnGap: '5px',
    },
    openLink: {
        display: 'inline',
    },
});

interface HistoryViewProps {
    kNr?: number;
    embedded?: boolean;
}

interface HistoryItem {
    type: 'angebot' | 'service' | 'auftrag' | 'lieferung' | 'rechnung' | 'attachment';
    datum: string;
    record: Angebot | Service | Auftrag | Lieferung | Rechnung | Attachment;
}

interface MappedHistoryItem {
    datum: string;
    type: HistoryItem['type'];
    kz: string;
    label: string;
    recordNr: string;
    recordUrl: string;
    status: string;
    text: string;
    file: string;
    fileUrl: string;
    anzeige: boolean;
    smRefType: string;
    smRef: string;
    smRefLabel: string;
    smRefView: ViewType;
    editData: Attachment | null;
}

export const HistoryView: React.FC<HistoryViewProps> = ({ kNr, embedded = false }) => {
    const { urlKundeNr } = useParams<RecordViewUrlParams>();
    const auth = useSelector(authSelector);
    const [showAngebote, setShowAngebote] = usePersistentState<boolean>(true, 'HistoryView.showAngebote');
    const [showServices, setShowServices] = usePersistentState<boolean>(true, 'HistoryView.showServices');
    const [showAuftraege, setShowAuftraege] = usePersistentState<boolean>(true, 'HistoryView.showAuftraege');
    const [showLieferungen, setShowLieferungen] = usePersistentState<boolean>(true, 'HistoryView.showLieferungen');
    const [showRechnungen, setShowRechnungen] = usePersistentState<boolean>(true, 'HistoryView.showRechnungen');
    const [showAttachments, setShowAttachments] = usePersistentState<boolean>(true, 'HistoryView.showRechnungen');
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);
    const [searchText, setSearchText] = React.useState('');
    const [editAttachmentData, setEditAttachmentData] = React.useState<Attachment | null>(null);
    const [deleteAttachmentData, setDeleteAttachmentData] = React.useState<Attachment | null>(null);
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const kundeList = useKundeList({
        nr: kNr,
        includeArchiv: true,
        withAngebotList: true,
        withServiceList: true,
        withAuftragList: true,
        withLieferungList: true,
        withRechnungList: true,
        withAttachmentList: true,
    });

    const historyItems = React.useMemo<HistoryItem[]>(() => {
        if (kundeList.isLoading || kundeList.isFetching || !kundeList.data || kundeList.data.length !== 1) {
            return [];
        }

        const kunde = kundeList.data[0];

        const items: HistoryItem[] = [
            ...(showAngebote ? kunde.angebotList || [] : []).map(
                (angebot) => ({ type: 'angebot', datum: angebot.datum, record: angebot } as HistoryItem),
            ),
            ...(showServices ? kunde.serviceList || [] : []).map(
                (service) => ({ type: 'service', datum: service.datum, record: service } as HistoryItem),
            ),
            ...(showAuftraege ? kunde.auftragList || [] : []).map(
                (auftrag) => ({ type: 'auftrag', datum: auftrag.datum, record: auftrag } as HistoryItem),
            ),
            ...(showLieferungen ? kunde.lieferungList || [] : []).map(
                (lieferung) => ({ type: 'lieferung', datum: lieferung.datum, record: lieferung } as HistoryItem),
            ),
            ...(showRechnungen ? kunde.rechnungList || [] : []).map(
                (rechnung) => ({ type: 'rechnung', datum: rechnung.datum, record: rechnung } as HistoryItem),
            ),
            ...(showAngebote || showAttachments ? kunde.attachmentList || [] : [])
                .filter(
                    (attachment) =>
                        (attachment.art !== 1 || attachment.file) && (showAttachments || attachment.art === 1),
                )
                .map(
                    (attachment) =>
                        ({ type: 'attachment', datum: attachment.datum, record: attachment } as HistoryItem),
                ),
        ];

        return items;
    }, [kundeList, showAngebote, showServices, showAttachments, showAuftraege, showLieferungen, showRechnungen]);

    const mappedHistoryItems = React.useMemo<MappedHistoryItem[]>(
        () =>
            historyItems.map((item) => {
                let kz = '';
                let label = '';
                let recordNr = '';
                let recordUrl = '';
                let status = '';
                let smRefType = '';
                let smRef = '';
                let text = '';
                let file = '';
                let fileUrl = '';
                let anzeige = true;
                let editData: Attachment | null = null;
                switch (item.type) {
                    case 'angebot':
                        {
                            const angebot = item.record as Angebot;
                            label = 'Angebot';
                            if (angebot.anlageUser) {
                                kz = betreuerByNr[angebot.anlageUser]?.kz || angebot.anlageUser.toString();
                            }
                            recordNr = angebot.nr;
                            recordUrl = createRecordUrl('angebote', kNr, angebot.nr);
                            status = AngebotStatusLabel[angebot.status] || angebot.status.toString();
                            text = angebot.aktText;
                            if (angebot.auNr) {
                                smRefType = 'auftrag';
                                smRef = angebot.auNr;
                            }
                        }
                        break;
                    case 'service':
                        {
                            const service = item.record as Service;
                            label = 'Service';
                            if (service.anlageUser) {
                                kz = betreuerByNr[service.anlageUser]?.kz || service.anlageUser.toString();
                            }
                            recordNr = service.nr;
                            recordUrl = createRecordUrl('services', kNr, service.nr);
                            status = ServiceStatusLabel[service.status] || service.status.toString();
                            text = service.aktText;
                            if (service.auNr) {
                                smRefType = 'auftrag';
                                smRef = service.auNr;
                            }
                        }
                        break;
                    case 'auftrag':
                        {
                            const auftrag = item.record as Auftrag;
                            if (auftrag.anlageUser) {
                                kz = betreuerByNr[auftrag.anlageUser]?.kz || auftrag.anlageUser.toString();
                            }
                            label = 'Auftrag';
                            recordNr = auftrag.nr;
                            recordUrl = createRecordUrl('auftraege', kNr, auftrag.nr);
                            status = AuftragStatusLabel[auftrag.status] || auftrag.status.toString();
                        }
                        break;
                    case 'lieferung':
                        {
                            const lieferung = item.record as Lieferung;
                            if (lieferung.anlageUser) {
                                kz = betreuerByNr[lieferung.anlageUser]?.kz || lieferung.anlageUser.toString();
                            }
                            label = 'Lieferung';
                            recordNr = lieferung.nr;
                            recordUrl = createRecordUrl('lieferungen', kNr, lieferung.nr);
                            status = LieferungStatusLabel[lieferung.status] || lieferung.status.toString();
                            if (lieferung.auNr) {
                                smRefType = 'auftrag';
                                smRef = lieferung.auNr;
                            }
                        }
                        break;
                    case 'rechnung':
                        {
                            const rechnung = item.record as Rechnung;
                            if (rechnung.anlageUser) {
                                kz = betreuerByNr[rechnung.anlageUser]?.kz || rechnung.anlageUser.toString();
                            }
                            label = 'Rechnung';
                            recordNr = rechnung.nr;
                            recordUrl = createRecordUrl('rechnungen', kNr, rechnung.nr);
                            status = RechnungStatusLabel[rechnung.status] || rechnung.status.toString();
                            if (rechnung.auNr) {
                                smRefType = 'auftrag';
                                smRef = rechnung.auNr;
                            }
                        }
                        break;
                    case 'attachment':
                        {
                            const attachment = item.record as Attachment;
                            if (attachment.updUser) {
                                kz = betreuerByNr[attachment.updUser]?.kz || attachment.updUser.toString();
                            }
                            label = AttachmentArtLabel[attachment.art];
                            if (attachment.art === 1) {
                                recordNr = attachment.smRef;
                                recordUrl = createRecordUrl('angebote', kNr, attachment.smRef);
                            } else if (attachment.art === 6) {
                                recordNr = attachment.smRef;
                                recordUrl = createRecordUrl('services', kNr, attachment.smRef);
                            }
                            text = attachment.text;
                            file = attachment.file;
                            fileUrl =
                                getApiUrl('attachment') + '/akte?kNr=' + kNr + '&fileName=' + encodeURIComponent(file);
                            editData = attachment;
                            if (attachment.smRefType && attachment.smRef) {
                                smRefType = attachment.smRefType;
                                smRef = attachment.smRef;
                            }
                            anzeige = attachment.anzeige;
                        }
                        break;
                    default:
                        break;
                }

                const smRefLabel = SmRefTypeLabel[smRefType] || '';
                const smRefView = SmRefTypeView[smRefType] || null;

                return {
                    datum: item.datum,
                    type: item.type,
                    kz,
                    label,
                    recordNr,
                    recordUrl,
                    status,
                    text,
                    file,
                    fileUrl,
                    editData,
                    anzeige,
                    smRefType,
                    smRef,
                    smRefLabel,
                    smRefView,
                };
            }),
        [betreuerByNr, historyItems, kNr],
    );

    const filteredMappedHistoryItems = React.useMemo(() => {
        const st = searchText.toLocaleLowerCase();

        let newFilteredMappedHistoryItems: MappedHistoryItem[];

        if (!st) {
            newFilteredMappedHistoryItems = mappedHistoryItems;
        } else {
            newFilteredMappedHistoryItems = mappedHistoryItems.filter(
                (item) =>
                    item.datum?.toLocaleLowerCase().includes(st) ||
                    item.kz?.toLocaleLowerCase().includes(st) ||
                    item.type?.toLocaleLowerCase().includes(st) ||
                    item.recordNr?.toLocaleLowerCase().includes(st) ||
                    item.status?.toLocaleLowerCase().includes(st) ||
                    item.smRef?.toLocaleLowerCase().includes(st) ||
                    item.text?.toLocaleLowerCase().includes(st) ||
                    item.file?.toLocaleLowerCase().includes(st),
            );
        }

        return newFilteredMappedHistoryItems.sort((a, b) => b.datum.localeCompare(a.datum));
    }, [mappedHistoryItems, searchText]);

    const onRenderCell = React.useCallback(
        (item: MappedHistoryItem | undefined, index: number | undefined): JSX.Element => {
            if (!item) {
                return <div />;
            }

            /*
            let kz = '';
            let label = '';
            let recordNr = '';
            let recordUrl = '';
            let status = '';
            let smRefType = '';
            let smRef = '';
            let text = '';
            let file = '';
            let fileUrl = '';
            let anzeige = true;
            let editData: Attachment | null = null;
            switch (item.type) {
                case 'angebot':
                    {
                        const angebot = item.record as Angebot;
                        label = 'Angebot';
                        if (angebot.anlageUser) {
                            kz = betreuerByNr[angebot.anlageUser]?.kz || angebot.anlageUser.toString();
                        }
                        recordNr = angebot.nr;
                        recordUrl = createRecordUrl('angebote', kNr, angebot.nr);
                        status = AngebotStatusLabel[angebot.status] || angebot.status.toString();
                        text = angebot.aktText;
                        if (angebot.auNr) {
                            smRefType = 'auftrag';
                            smRef = angebot.auNr;
                        }
                    }
                    break;
                case 'service':
                    {
                        const service = item.record as Service;
                        label = 'Service';
                        if (service.anlageUser) {
                            kz = betreuerByNr[service.anlageUser]?.kz || service.anlageUser.toString();
                        }
                        recordNr = service.nr;
                        recordUrl = createRecordUrl('services', kNr, service.nr);
                        status = ServiceStatusLabel[service.status] || service.status.toString();
                        text = service.aktText;
                        if (service.auNr) {
                            smRefType = 'auftrag';
                            smRef = service.auNr;
                        }
                    }
                    break;
                case 'auftrag':
                    {
                        const auftrag = item.record as Auftrag;
                        if (auftrag.anlageUser) {
                            kz = betreuerByNr[auftrag.anlageUser]?.kz || auftrag.anlageUser.toString();
                        }
                        label = 'Auftrag';
                        recordNr = auftrag.nr;
                        recordUrl = createRecordUrl('auftraege', kNr, auftrag.nr);
                        status = AuftragStatusLabel[auftrag.status] || auftrag.status.toString();
                    }
                    break;
                case 'lieferung':
                    {
                        const lieferung = item.record as Lieferung;
                        if (lieferung.anlageUser) {
                            kz = betreuerByNr[lieferung.anlageUser]?.kz || lieferung.anlageUser.toString();
                        }
                        label = 'Lieferung';
                        recordNr = lieferung.nr;
                        recordUrl = createRecordUrl('lieferungen', kNr, lieferung.nr);
                        status = LieferungStatusLabel[lieferung.status] || lieferung.status.toString();
                        if (lieferung.auNr) {
                            smRefType = 'auftrag';
                            smRef = lieferung.auNr;
                        }
                    }
                    break;
                case 'rechnung':
                    {
                        const rechnung = item.record as Rechnung;
                        if (rechnung.anlageUser) {
                            kz = betreuerByNr[rechnung.anlageUser]?.kz || rechnung.anlageUser.toString();
                        }
                        label = 'Rechnung';
                        recordNr = rechnung.nr;
                        recordUrl = createRecordUrl('rechnungen', kNr, rechnung.nr);
                        status = RechnungStatusLabel[rechnung.status] || rechnung.status.toString();
                        if (rechnung.auNr) {
                            smRefType = 'auftrag';
                            smRef = rechnung.auNr;
                        }
                    }
                    break;
                case 'attachment':
                    {
                        const attachment = item.record as Attachment;
                        if (attachment.updUser) {
                            kz = betreuerByNr[attachment.updUser]?.kz || attachment.updUser.toString();
                        }
                        label = AttachmentArtLabel[attachment.art];
                        if (attachment.art === 1) {
                            recordNr = attachment.smRef;
                            recordUrl = createRecordUrl('angebote', kNr, attachment.smRef);
                        } else if (attachment.art === 6) {
                            recordNr = attachment.smRef;
                            recordUrl = createRecordUrl('services', kNr, attachment.smRef);
                        }
                        text = attachment.text;
                        file = attachment.file;
                        fileUrl =
                            getApiUrl('attachment') + '/akte?kNr=' + kNr + '&fileName=' + encodeURIComponent(file);
                        editData = attachment;
                        if (attachment.smRefType && attachment.smRef) {
                            smRefType = attachment.smRefType;
                            smRef = attachment.smRef;
                        }
                        anzeige = attachment.anzeige;
                    }
                    break;
                default:
                    break;
            }

            const smRefLabel = SmRefTypeLabel[smRefType] || '';
            const smRefView = SmRefTypeView[smRefType] || null;

             */

            return (
                <div className={styles.listItem} key={item.datum + '|' + item.type + '|' + index}>
                    <div>{moment(item.datum).format('DD.MM.YYYY HH:mm:ss')}</div>
                    <div>{item.kz}</div>
                    <div>{item.label}</div>
                    <div className={styles.listCell}>
                        {item.recordNr && <OpenLink link={item.recordUrl} label={item.recordNr} />}
                    </div>
                    <div>{item.status}</div>
                    {item.smRefView && item.smRef ? (
                        <div className={styles.listCell}>
                            {item.smRefLabel + ' '}
                            <OpenLink
                                className={styles.openLink}
                                link={createRecordUrl(item.smRefView, kNr, item.smRef)}
                                label={item.smRef}
                            />
                        </div>
                    ) : (
                        <div />
                    )}
                    <div>{item.text}</div>
                    <div>
                        {item.file && (
                            <>
                                <a href={item.fileUrl} rel="noreferrer" target="_blank">
                                    {item.file}
                                </a>
                                {item.anzeige && (auth.rolle === 'GF' || auth.rolle === 'ID')
                                    ? ' (für AD sichtbar)'
                                    : ''}
                            </>
                        )}
                    </div>
                    <div className={styles.actionButtons}>
                        {(auth.rolle === 'GF' || auth.rolle === 'ID') && item.editData && (
                            <>
                                <EditIcon
                                    className={formStyles.positionenAction}
                                    onClick={() => setEditAttachmentData(item.editData)}
                                />
                                <DeleteIcon
                                    className={formStyles.positionenAction}
                                    onClick={() => setDeleteAttachmentData(item.editData)}
                                />
                            </>
                        )}
                    </div>
                </div>
            );
        },
        [auth.rolle, kNr],
    );

    return (
        <Fabric className={classnames(viewStyles.container, { [viewStyles.embedded]: embedded })}>
            <Helmet>
                <title>
                    {urlKundeNr ? 'Kunde ' + urlKundeNr + ' | ' : ''}
                    Historie
                </title>
            </Helmet>
            {kundeList.isLoading || kundeList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.searchBar}>
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Angebote"
                            checked={showAngebote}
                            onChange={(event, checked) => {
                                setShowAngebote(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Services"
                            checked={showServices}
                            onChange={(event, checked) => {
                                setShowServices(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Aufträge"
                            checked={showAuftraege}
                            onChange={(event, checked) => {
                                setShowAuftraege(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Lieferungen"
                            checked={showLieferungen}
                            onChange={(event, checked) => {
                                setShowLieferungen(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Rechnungen"
                            checked={showRechnungen}
                            onChange={(event, checked) => {
                                setShowRechnungen(!!checked);
                            }}
                        />
                        <Checkbox
                            className={formStyles.checkboxInsideForm}
                            label="Dokumente"
                            checked={showAttachments}
                            onChange={(event, checked) => {
                                setShowAttachments(!!checked);
                            }}
                        />
                        <SearchBox
                            value={searchText}
                            onChange={(event, newValue): void => {
                                setSearchText(newValue ?? '');
                            }}
                            placeholder="Suche"
                            autoFocus
                        />

                        <div />
                        {(auth.rolle === 'GF' || auth.rolle === 'ID') && kNr && (
                            <PrimaryButton onClick={() => setShowAddAttachments(true)} text="Dokumente anhängen" />
                        )}
                    </div>
                    <div className={viewStyles.listContainer}>
                        <div className={classnames(styles.listItem, styles.listHeader)}>
                            <div>Datum</div>
                            <div>Benutzer</div>
                            <div>Art</div>
                            <div>Nummer</div>
                            <div>Status</div>
                            <div>Referenz</div>
                            <div>Text</div>
                            <div>Dokument</div>
                            <div />
                        </div>
                        {historyItems.length === 0 ? (
                            <div className={viewStyles.noData}>Keine Einträge.</div>
                        ) : (
                            <AutoSizer>
                                {({ height, width }) => (
                                    <div style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}>
                                        <List items={filteredMappedHistoryItems} onRenderCell={onRenderCell} />
                                    </div>
                                )}
                            </AutoSizer>
                        )}
                    </div>
                </>
            )}
            {showAddAttachments && kNr && (
                <AddAttachments
                    kNr={kNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kundeList.refetch();
                        }
                    }}
                />
            )}
            {editAttachmentData && (
                <EditAttachment
                    attachment={editAttachmentData}
                    onClose={(refetch) => {
                        if (refetch) {
                            kundeList.refetch();
                        }
                        setEditAttachmentData(null);
                    }}
                />
            )}
            {deleteAttachmentData && (
                <ConfirmMessage
                    title="Dokument löschen"
                    message={
                        'Soll das Dokument ' +
                        deleteAttachmentData.file +
                        ' / ' +
                        deleteAttachmentData.text +
                        '  gelöscht werden?'
                    }
                    onConfirm={() => {
                        if (deleteAttachmentData) {
                            deleteAttachment(deleteAttachmentData).then(() => {
                                kundeList.refetch();
                                setDeleteAttachmentData(null);
                            });
                        }
                    }}
                    onCancel={() => setDeleteAttachmentData(null)}
                />
            )}
        </Fabric>
    );
};
